<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import {QForm} from 'quasar';
import {useI18n} from 'vue-i18n';
import {useValidateVoucher} from '@/ts/composables/stateful/use-validate-voucher';
import {useRequiredRule} from '@/ts/composables/pure/use-input-rules';
import type {ValidateVoucherResponse} from '@/ts/types/dto/sign-in-dto';

const voucherCode = defineModel<string>('voucherCode');
// Two-way so the page can collapse it back to the trial view on Back.
const voucherRevealed = defineModel<boolean>('voucherRevealed', {default: false});

const emit = defineEmits<{
  voucherValidated: [voucherCode: string, response: ValidateVoucherResponse],
}>();

const {t} = useI18n();
const form = ref<QForm>(null!);
const requiredRule = useRequiredRule();

// Short inline error shown below the field; set when validation fails on
// Continue and cleared as soon as the user edits the code. (The longer
// guidance message is also shown as a toast by validateVoucher.)
const voucherError = ref('');
const voucherRules = computed((): ((val: string) => boolean | string)[] => [
  requiredRule,
  (): boolean | string => voucherError.value || true,
]);

const {
  isValidated,
  validateVoucher,
  resetValidation,
} = useValidateVoucher();

const revealVoucher = (): void => {
  voucherRevealed.value = true;
};

function onVoucherInput(): void {
  voucherError.value = '';
  resetValidation();
  form.value?.resetValidation();
}

// When the voucher view is hidden (collapsed on Back, or reset on re-entering
// the step), drop the entered code and its validation so it never reappears
// pre-populated — and can't leak into a "continue with free trial" submit.
watch(voucherRevealed, (revealed) => {
  if (!revealed) {
    voucherCode.value = '';
    voucherError.value = '';
    resetValidation();
  }
}, {immediate: true});

/** Validates the voucher form (if shown). Returns true if the step is ready to proceed. */
async function validateForm(): Promise<boolean> {
  if (voucherRevealed.value) {
    const formValid = await form.value.validate();
    if (!formValid || !voucherCode.value) {
      return false;
    }
    if (!isValidated.value) {
      try {
        const response = await validateVoucher(voucherCode.value.trim());
        emit('voucherValidated', voucherCode.value.trim(), response);
      } catch {
        // Keep the entered code and show a short inline error (validateVoucher
        // also surfaces the longer guidance message as a toast).
        voucherError.value = t('invalid_voucher');
        await form.value.validate();
        return false;
      }
    }
  }
  return true;
}

defineExpose({
  validateForm,
});
</script>
<template>
  <div class="sign-up-trial">
    <template v-if="!voucherRevealed">
      <h1>{{ $t('start_with_free_trial') }}</h1>
      <p class="trial-text">
        {{ $t('start_with_free_trial_text') }}
      </p>
      <q-btn
        flat
        :ripple="false"
        class="voucher-link"
        :label="$t('i_have_a_voucher')"
        @click="revealVoucher"
      />
    </template>

    <q-form v-else ref="form" @submit.prevent>
      <h1>{{ $t('redeem_a_voucher') }}</h1>
      <p class="trial-text">
        {{ $t('redeem_a_voucher_text') }}
      </p>
      <q-input
        v-model="voucherCode"
        :rules="voucherRules"
        lazy-rules="ondemand"
        :label="$t('voucher_code')"
        @update:model-value="onVoucherInput"
      />
    </q-form>
  </div>
</template>
<style scoped lang="scss">
h1 {
  @include font-display-4;
  margin-bottom: var(--Spacing-spacing-6);
}

.sign-up-trial {
  display: flex;
  flex-direction: column;
}

.trial-text {
  margin-bottom: var(--Spacing-spacing-6);
}

.voucher-link {
  @include focus-visible;
  align-self: flex-start;
  text-decoration: underline;
  font-size: var(--Typography-Body);
  text-transform: none;
  border-radius: var(--button-border-radius);

  // Plain text link per the design: the underline is the affordance, no
  // hover/press tint (keyboard focus stays via the focus-visible outline).
  :deep(.q-focus-helper) {
    display: none;
  }
}
</style>
